import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { createSelector } from "reselect";
import { v4 as uuidv4 } from "uuid";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  NavItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  NavLink,
  Spinner,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  submitBybitApiData,
  resetSubmitBybitApiDataSuccess,
  fetchApiData,
  deleteApiData,
} from "../../store/exchange/actions";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Enhanced selector to include fetched API data
const selectBybitApiDataState = createSelector(
  (state) =>
    state.BybitConnection || {
      bybitApiData: [],
      error: null,
      loading: false,
      success: false,
      fetchedData: [], // Make sure the initial state includes fetchedData as an empty array
    },

  (bybitApiDataReducer) => {
    const transformedData =
      bybitApiDataReducer.fetchedData?.map((item) => item) || []; // Ensure that transformedData is an array, even if fetchedData is undefined
    return {
      ...bybitApiDataReducer,
      fetchedData: transformedData,
    };
  }
);

const BybitConnection = () => {
  document.title = "API Connection | Bybit";

  const [activeTab, setActiveTab] = useState(1);
  const [formData, setFormData] = useState({
    apiName: "",
    apiKey: "",
    apiSecret: "",
    securityToken: uuidv4().slice(0, 32),
    type: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, success, fetchedData } = useSelector(
    selectBybitApiDataState
  );

  useEffect(() => {
    if (success) {
      toast.success("Bybit API Data Submitted Successfully", {
        autoClose: 2000,
      });
      dispatch(resetSubmitBybitApiDataSuccess()); // Reset success state
      navigate("/dashboard"); // Redirect after the toast is closed
    }
  }, [success, navigate, dispatch]);

  const toggleTab = (tab) => {
    if (tab >= 1 && tab <= 5) {
      setActiveTab(tab);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const submitForm = () => {
    dispatch(submitBybitApiData(formData)); // Dispatching the action to submit form data
  };

  const copyIpAddress = (e) => {
    const ip = e.target.getAttribute("data-ip");
    navigator.clipboard
      .writeText(ip)
      .then(() => toast.success(`IP address ${ip} copied`, { autoClose: 2000 }))
      .catch(() =>
        toast.error("Failed to copy IP address", { autoClose: 2000 })
      );
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Step 2: Create functions to show and hide the modal
  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setItemToDelete(null);
  };

  useEffect(() => {
    dispatch(fetchApiData()); // Fetch API data when the component mounts
  }, [dispatch]);

  useEffect(() => {}, [fetchedData]);
  // Step 3: Create a function to handle the delete action

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      // Call the delete action here (dispatch or API call)
      try {
        await dispatch(deleteApiData(itemToDelete.security_token));
        await dispatch(fetchApiData());
        toast.success("API data deleted successfully", { autoClose: 3000 });
      } catch (error) {
        toast.error("Failed to delete API data", { autoClose: 2000 });
      }
    }
    closeDeleteModal(); // Close the modal after deletion
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="API Connection"
            breadcrumbItem="Bybit Connection"
          />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div>
                    {/* Your other component elements */}
                    <button
                      type="button"
                      className="btn btn-light btn-label mb-4"
                      id="copyIpButton"
                      data-ip="45.133.178.164"
                      onClick={copyIpAddress} // Attach the copy function here
                    >
                      <i className="bx bx-copy-alt label-icon"></i>{" "}
                      45.133.178.164
                    </button>
                  </div>

                  <div className="wizard clearfix ">
                    <div className="steps clearfix mb-4">
                      <ul>
                        {/* Step 1: API Name */}
                        <NavItem
                          className={classnames({ current: activeTab === 1 })}
                        >
                          <NavLink
                            className={classnames({ active: activeTab === 1 })}
                            onClick={() => toggleTab(1)}
                          >
                            <span className="number">1.</span> API Name
                          </NavLink>
                        </NavItem>
                        {/* Step 2: API Key */}
                        <NavItem
                          className={classnames({ current: activeTab === 2 })}
                        >
                          <NavLink
                            className={classnames({ active: activeTab === 2 })}
                            onClick={() => toggleTab(2)}
                          >
                            <span className="number">2.</span> API Key
                          </NavLink>
                        </NavItem>
                        {/* Step 3: API Secret */}
                        <NavItem
                          className={classnames({ current: activeTab === 3 })}
                        >
                          <NavLink
                            className={classnames({ active: activeTab === 3 })}
                            onClick={() => toggleTab(3)}
                          >
                            <span className="number">3.</span> API Secret
                          </NavLink>
                        </NavItem>
                        {/* Step 4: Security Token */}
                        <NavItem
                          className={classnames({ current: activeTab === 4 })}
                        >
                          <NavLink
                            className={classnames({ active: activeTab === 4 })}
                            onClick={() => toggleTab(4)}
                          >
                            <span className="number">4.</span> Security Token
                          </NavLink>
                        </NavItem>
                        {/* Step 5: Type */}
                        <NavItem
                          className={classnames({ current: activeTab === 5 })}
                        >
                          <NavLink
                            className={classnames({ active: activeTab === 5 })}
                            onClick={() => toggleTab(5)}
                          >
                            <span className="number">5.</span> Type
                          </NavLink>
                        </NavItem>
                      </ul>
                    </div>
                    <TabContent activeTab={activeTab} className="body">
                      {/* API Name */}
                      <TabPane tabId={1}>
                        <Form>
                          <FormGroup>
                            <Input
                              type="text"
                              name="apiName"
                              id="apiName"
                              value={formData.apiName}
                              onChange={handleInputChange}
                              placeholder="Enter API Name"
                            />
                          </FormGroup>
                          <div className="text-right">
                            <Button
                              color="primary"
                              onClick={() => toggleTab(2)}
                            >
                              Next
                            </Button>
                          </div>
                        </Form>
                      </TabPane>
                      {/* API Key */}
                      <TabPane tabId={2}>
                        <Form>
                          <FormGroup>
                            <Input
                              type="text"
                              name="apiKey"
                              id="apiKey"
                              value={formData.apiKey}
                              onChange={handleInputChange}
                              placeholder="Enter API Key"
                            />
                          </FormGroup>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              color="primary"
                              onClick={() => toggleTab(1)}
                            >
                              Previous
                            </Button>
                            <Button
                              color="primary"
                              onClick={() => toggleTab(3)}
                            >
                              Next
                            </Button>
                          </div>
                        </Form>
                      </TabPane>
                      {/* API Secret */}
                      <TabPane tabId={3}>
                        <Form>
                          <FormGroup>
                            <Input
                              type="text"
                              name="apiSecret"
                              id="apiSecret"
                              value={formData.apiSecret}
                              onChange={handleInputChange}
                              placeholder="Enter API Secret"
                            />
                          </FormGroup>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              color="primary"
                              onClick={() => toggleTab(2)}
                            >
                              Previous
                            </Button>
                            <Button
                              color="primary"
                              onClick={() => toggleTab(4)}
                            >
                              Next
                            </Button>
                          </div>
                        </Form>
                      </TabPane>
                      {/* Security Token */}
                      <TabPane tabId={4}>
                        <Form>
                          <FormGroup>
                            <Input
                              type="text"
                              name="securityToken"
                              id="securityToken"
                              value={formData.securityToken}
                              readOnly // This field is auto-generated and read-only
                            />
                          </FormGroup>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              color="primary"
                              onClick={() => toggleTab(3)}
                            >
                              Previous
                            </Button>
                            <Button
                              color="primary"
                              onClick={() => toggleTab(5)}
                            >
                              Next
                            </Button>
                          </div>
                        </Form>
                      </TabPane>
                      {/* Type */}
                      <TabPane tabId={5}>
                        <Form>
                          <FormGroup>
                            <Input
                              type="select"
                              name="type"
                              id="typeSelect"
                              value={formData.type}
                              onChange={handleInputChange}
                            >
                              <option value="">Select Type</option>
                              <option value="live">Live Trading</option>
                              <option value="demo">Demo Trading</option>
                            </Input>
                          </FormGroup>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              color="primary"
                              onClick={() => toggleTab(4)}
                            >
                              Previous
                            </Button>
                            {/* Submit Button */}

                            <Button
                              color="primary"
                              onClick={submitForm}
                              disabled={loading}
                            >
                              {loading ? <Spinner size="sm" /> : "Submit"}
                            </Button>
                          </div>
                        </Form>
                      </TabPane>
                    </TabContent>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>API Name</th>
                  <th>API Key</th>
                  <th>Security Token</th>
                  <th>Type</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {fetchedData && fetchedData.length > 0 ? (
                  fetchedData.map((data, index) => (
                    <tr key={index}>
                      <td>{data.api_name || "N/A"}</td>
                      <td>{data.api_key || "N/A"}</td>
                      <td>{data.security_token || "N/A"}</td>
                      <td>{data.exchange_type || "N/A"}</td>
                      <td>
                        <Link
                          to="#"
                          className="text-danger"
                          onClick={() => openDeleteModal(data)} // Step 4: Trigger the modal
                        >
                          <i className="mdi mdi-delete font-size-18" />
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">No API Data found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Container>
      </div>
      <ToastContainer />
      <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal}>
        <ModalHeader toggle={closeDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>
          {itemToDelete && (
            <p>
              Are you sure you want to delete API data with Security Token:{" "}
              {itemToDelete.security_token}?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirmDelete}>
            Confirm Delete
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default BybitConnection;
